import { DropdownGroup, DropDownItem } from '../../ui/select/types/dropdownItem';
import {
  OrderWidgetConfigurationItem,
} from '../../../store/landingpage/types';
import { LOCATION_TO_LOCALE_MAP } from '../../../router/locations';
import { ProductServerLocation } from '../../../store/product/types';

export function getFormattedRamByUnitsAndLocale(
  units: number,
  locale: string,
): string {
  if (units < 0) {
    throw new TypeError('Units must be positive!');
  }

  const amount = new Intl.NumberFormat(locale, { style: 'decimal' })
    .format(0.25 * units);

  return `${amount} GB`;
}

export function getWidgetTitle(
  location: ProductServerLocation,
  soldOutTitle: string,
): string {
  return location.available
    ? `${location.country} (${location.city})`
    : `${location.country} (${location.city}) - ${soldOutTitle}`;
}

export function convertLocationToDropdownItem(
  location: ProductServerLocation,
  soldOutTitle: string,
): DropDownItem<string> {
  return {
    title: getWidgetTitle(location, soldOutTitle),
    value: location.key,
    disabled: !location.available,
  };
}

export function groupOrderConfigurationByRegion(
  orderWidgetConfiguration: OrderWidgetConfigurationItem,
  selectedLocation: ProductServerLocation,
  texts: {
    soldOutTitle: string;
    currentServerRegionTitle: string;
    otherServerRegionTitle: string;
  },
): Array<DropdownGroup<string>> {
  return [
    {
      group: texts.currentServerRegionTitle,
      items: orderWidgetConfiguration.locations
        .filter((location) => !location.switchRegion
          && location.key !== selectedLocation.key)
        .map((location) => convertLocationToDropdownItem(location, texts.soldOutTitle)),
    },
    {
      group: texts.otherServerRegionTitle,
      items: orderWidgetConfiguration.locations
        .filter((location) => location.switchRegion
          && location.key !== selectedLocation.key)
        .map((location) => convertLocationToDropdownItem(location, texts.soldOutTitle)),
    },
  ];
}

export function getDefaultOrderWidgetLocation(
  orderWidgetConfiguration: OrderWidgetConfigurationItem,
): ProductServerLocation {
  return orderWidgetConfiguration.locations.find((l) => l !== undefined);
}

export function hasOrderConfigurationActiveLocations(
  dropDownItems: Array<DropdownGroup<string>>,
): boolean {
  return !dropDownItems.some((group) => group.items.length && group.items.some(
    (item) => !item.disabled,
  ));
}

export function getDefaultLocationDropdownItem(
  prefix: {
    region: string;
    locale: string;
  },
  locations: ProductServerLocation[],
  soldOutTitle: string,
): DropDownItem<string> {
  // let dropdownItem: DropDownItem<number> = null;
  let defaultLocation: ProductServerLocation = null;

  if (!locations.length) {
    return undefined;
  }

  if ((defaultLocation = getLocationByRegionAndLocalePrefix(prefix, locations)) !== undefined) {
    return convertLocationToDropdownItem(defaultLocation, soldOutTitle);
  }

  if ((defaultLocation = getDefaultAvailableLocationWithoutRegionSwitch(locations)) !== undefined) {
    return convertLocationToDropdownItem(defaultLocation, soldOutTitle);
  }

  if ((defaultLocation = getAvailableLocationWithoutRegionSwitch(locations)) !== undefined) {
    return convertLocationToDropdownItem(defaultLocation, soldOutTitle);
  }

  if ((defaultLocation = getAvailableFirstLocation(locations)) !== undefined) {
    return convertLocationToDropdownItem(defaultLocation, soldOutTitle);
  }

  return convertLocationToDropdownItem(
    getRandomLocation(locations),
    soldOutTitle,
  );
}

export function getRandomLocation(
  locations: ProductServerLocation[],
): ProductServerLocation|undefined {
  return !locations.length ? undefined : locations[Math.floor(Math.random() * locations.length)];
}

export function getLocationByRegionAndLocalePrefix(
  prefix: {
    region: string;
    locale: string;
  }, locations: ProductServerLocation[],
): ProductServerLocation|undefined {
  const locationKey: string = prefix.region in LOCATION_TO_LOCALE_MAP
    ? LOCATION_TO_LOCALE_MAP[prefix.region].get(prefix.locale)
    : null;

  if (locationKey === null) {
    return undefined;
  }

  return locations.find((location) => locationKey === location.key && location.available);
}

export function getDefaultAvailableLocationWithoutRegionSwitch(
  locations: ProductServerLocation[],
): ProductServerLocation|undefined {
  return locations.find(
    (location) => location.default
      && location.available
      && !location.switchRegion,
  );
}

export function getAvailableLocationWithoutRegionSwitch(
  locations: ProductServerLocation[],
): ProductServerLocation|undefined {
  return locations.find((location) => location.available && !location.switchRegion);
}

export function getAvailableFirstLocation(
  locations: ProductServerLocation[],
): ProductServerLocation|undefined {
  return locations.find((location) => location.available);
}
